import React, { FunctionComponent, useMemo } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DetailsRow,
  IColumn,
  Link,
  ScrollablePane,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { ScrollListener } from "@components/tables/ScrollListener";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { BpIdAuditLogDto } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useBpIdInviteAuditLogs } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { config } from "@libs/config/config";
import { getPagesResult } from "@libs/paging/paging.utils";

import { TenantLink } from "./TenantLink";
import { UserLink } from "./UserLink";

interface BpIdInviteAuditLogListProps {
  invitationId: string;
}
export const BpIdInviteAuditLogList: FunctionComponent<BpIdInviteAuditLogListProps> = ({
  invitationId
}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage
  } = useBpIdInviteAuditLogs(invitationId);

  const theme = useTheme();
  const items = useMemo(() => getPagesResult(data), [data]);
  const ToolTipContent: FunctionComponent<BpIdAuditLogDto> = item => (
    <Stack>
      {item.journey && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Journey</Text>
          <Text>{item.journey}</Text>
        </Stack>
      )}
      {item.policyName && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Policy name</Text>
          <Text>{item.policyName}</Text>
        </Stack>
      )}

      {item.step && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Step</Text>
          <Text>{item.step}</Text>
        </Stack>
      )}
      {Object.keys(item.properties).map(x => (
        <Stack key={x} horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>{x}</Text>
          <Text>{item.properties[x]}</Text>
        </Stack>
      ))}
    </Stack>
  );

  const columns: IColumn[] = [
    {
      key: "timestamp",
      name: "On",
      maxWidth: 180,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) =>
        item.timeStamp
          ? DateTime.fromISO(item.timeStamp).toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT
            )
          : "-"
    },

    {
      key: "tenant",
      name: "Tenant",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <TenantLink tenantId={item.tenantId} />
      )
    },
    {
      key: "userId",
      name: "User",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <UserLink bpIdUserId={item.bpIdUserId} />
      )
    },
    {
      key: "source",
      name: "Source",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => item.source
    },
    {
      key: "severity",
      name: "Severity",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <TooltipHost content={item.severity}>{item.severity}</TooltipHost>
      )
    },
    {
      key: "eventType",
      name: "Event type",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <TooltipHost content={<ToolTipContent {...item} />}>
          {item.eventType}
        </TooltipHost>
      )
    },
    {
      key: "aventAction",
      name: "Event Action",
      maxWidth: 170,
      minWidth: 140,
      onRender: (item: BpIdAuditLogDto) => item.eventAction
    },
    {
      key: "errorCode",
      name: "Error Code",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) =>
        `${item.errorCode ?? ""} ${item.errorMessage ?? ""}`
    },
    {
      key: "b2cCorrelationId",
      name: "B2C Correlation Id",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) => {
        if (item.timeStamp && item.b2cCorrelationId) {
          const from = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.minus({ minute: 2 })
            ?.toISO();

          const to = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.plus({ minute: 2 })
            ?.toISO();
          return (
            <Link
              href={`${config.seqUrl}?filter=B2CCorrelationId%20%3D%20'${item.b2cCorrelationId}'&from=${from}&to=${to}`}
              target="_blank"
              rel="noreferrer"
            >
              {item.b2cCorrelationId}
            </Link>
          );
        }
      }
    },
    {
      key: "correlationId",
      name: " Correlation Id",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) => {
        if (item.timeStamp && item.correlationId) {
          const from = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.minus({ minute: 2 })
            ?.toISO();

          const to = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.plus({ minute: 2 })
            ?.toISO();

          const split = item.correlationId.split(":");
          const correlationId = split.length > 0 ? split[0] : "";
          const correctedCorrelationId = correlationId.replace("+", "%2B");
          return (
            <Link
              href={`${config.seqUrl}?filter=BpTraceRootId%20%3D%20'${correctedCorrelationId}'&from=${from}&to=${to}`}
              target="_blank"
              rel="noreferrer"
            >
              {item.correlationId}
            </Link>
          );
        }
      }
    }
  ];

  const handleScrolledToBottom = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return (
    <Stack styles={{ root: { position: "relative", height: 200 } }}>
      <ScrollablePane>
        <ShimmeredDetailsList
          items={items}
          enableShimmer={isLoading}
          errorMessage={error?.message}
          columns={columns}
          stickyHeader
          onRenderRow={row => {
            const item: BpIdAuditLogDto = row?.item;
            const styles = item.success
              ? {}
              : {
                  root: {
                    backgroundColor: theme.semanticColors.errorBackground
                  }
                };
            return row ? (
              <Stack styles={styles}>
                <DetailsRow styles={styles} {...row} />
              </Stack>
            ) : null;
          }}
        />
        <ScrollListener
          hasNextPage={true}
          onScrolledToBottom={handleScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
